import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'convex/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../convex/_generated/api';
import { Button } from '../../catalyst-components/button';
import { Checkbox, CheckboxField } from '../../catalyst-components/checkbox';
import { MainRoutes } from '../../shared/models';
import { BaseContext } from '../../shared/base-context';
import { useStableQuery } from '../../shared/hooks/useStableQuery';
import Markdown from 'react-markdown';

export const LegalAcceptance: React.FC = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const {convexUserId} = useContext(BaseContext);

  // Queries to check if user needs to accept latest versions
  const disclaimerStatus = useStableQuery(api.disclaimers.needsDisclaimerAcceptance, convexUserId ? { user_id: convexUserId } : 'skip');
  const privacyPolicyStatus = useStableQuery(api.privacy_policy.needsPrivacyPolicyAcceptance, convexUserId ? { user_id: convexUserId } : 'skip');

  // Mutations to accept latest versions
  const acceptDisclaimer = useMutation(api.disclaimers.acceptLatestDisclaimer);
  const acceptPrivacyPolicy = useMutation(api.privacy_policy.acceptLatestPrivacyPolicy);

  const needsDisclaimer = disclaimerStatus?.needsAcceptance ?? false;
  const needsPrivacyPolicy = privacyPolicyStatus?.needsAcceptance ?? false;
  const latestDisclaimer = disclaimerStatus?.latestDisclaimer;
  const latestPrivacyPolicy = privacyPolicyStatus?.latestPolicy;


  // Redirect if no acceptance is needed
  useEffect(() => {
    if (disclaimerStatus && privacyPolicyStatus && !needsDisclaimer && !needsPrivacyPolicy) {
      navigate(MainRoutes.Progress, { replace: true });
    }
  }, [disclaimerStatus, privacyPolicyStatus, needsDisclaimer, needsPrivacyPolicy, navigate]);

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      // Accept disclaimer if needed
      if (needsDisclaimer) {
        await acceptDisclaimer({ user_id: convexUserId });
      }

      // Accept privacy policy if needed
      if (needsPrivacyPolicy) {
        await acceptPrivacyPolicy({ user_id: convexUserId });
      }

      // Redirect to progress after successful acceptance
      navigate(MainRoutes.Progress, { replace: true });
    } catch (error) {
      // You might want to show an error message to the user
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
  }, [])

  // Show loading while checking status
  if (!disclaimerStatus || !privacyPolicyStatus) {
    return (
      <div className="min-h-screen bg-primary-1 flex items-center justify-center">
        <div className="text-center">
          <img src="/boar_track_v2_a_png.png" alt="Loading..." className="h-12 w-12 mx-auto" />
          <p className="mt-4 text-primary-4">{t('legal.loadingMessage')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-1">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-5 mb-2">
            {t('legal.pageTitle')}
          </h1>
          <p className="text-lg text-primary-4">
            {t('legal.pageDescription')}
          </p>
        </div>

        {/* Legal Documents */}
        <div className="space-y-8">
          {/* Disclaimer Section */}
          {needsDisclaimer && latestDisclaimer && (
            <div className="bg-primary-1 p-6">
                <h2 className="text-2xl font-bold mb-4">{t('legal.disclaimer')}</h2>
                <Markdown>{latestDisclaimer.content}</Markdown>
            </div>
          )}

          {/* Privacy Policy Section */}
          {needsPrivacyPolicy && latestPrivacyPolicy && (
            <div className="bg-primary-1 p-6">
                <h2 className="text-2xl font-bold mb-4">{t('legal.privacyPolicy')}</h2>
                <Markdown>
                    {latestPrivacyPolicy.content}
                </Markdown>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-center">
          <Button
            onClick={handleAccept}
            disabled={isSubmitting}
            color="light"
            className="px-8 py-3 text-lg font-medium"
          >
            {isSubmitting ? t('legal.acceptingButton') : t('legal.acceptButton')}
          </Button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-primary-4">
            {t('legal.helpText')}
          </p>
        </div>
      </div>
    </div>
  );
};
